import { useRef, useCallback } from 'react';
import type { Position, Device, Trip, Stop } from '@/lib/traccar-api';

interface DeviceActivityState {
  isMoving: boolean;
  currentTripStart?: {
    time: string;
    lat: number;
    lon: number;
    address?: string;
  };
  currentStopStart?: {
    time: string;
    lat: number;
    lon: number;
    address?: string;
  };
  lastPosition?: Position;
  accumulatedDistance: number;
  maxSpeed: number;
  speedSum: number;
  speedCount: number;
}

// Haversine formula to calculate distance between two coordinates in meters
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MOVEMENT_THRESHOLD = 2; // km/h - speed above this means moving

export function useRealtimeActivity() {
  const deviceStatesRef = useRef<Map<number, DeviceActivityState>>(new Map());
  
  const processPositionUpdates = useCallback((
    positions: Position[],
    devices: Device[],
    currentTrips: Trip[],
    currentStops: Stop[],
  ): { trips: Trip[]; stops: Stop[] } => {
    const deviceMap = new Map(devices.map(d => [d.id, d]));
    let newTrips = currentTrips;
    let newStops = currentStops;
    let tripsChanged = false;
    let stopsChanged = false;

    const mutateTrips = () => { if (!tripsChanged) { newTrips = [...currentTrips]; tripsChanged = true; } };
    const mutateStops = () => { if (!stopsChanged) { newStops = [...currentStops]; stopsChanged = true; } };

    positions.forEach(position => {
      const device = deviceMap.get(position.deviceId);
      if (!device) return;
      
      let state = deviceStatesRef.current.get(position.deviceId);
      if (!state) {
        state = {
          isMoving: false,
          accumulatedDistance: 0,
          maxSpeed: 0,
          speedSum: 0,
          speedCount: 0,
        };
        deviceStatesRef.current.set(position.deviceId, state);
      }
      
      const isNowMoving = position.speed >= MOVEMENT_THRESHOLD;
      const wasMoving = state.isMoving;
      
      // Calculate distance from last position
      if (state.lastPosition && position.valid) {
        const distance = calculateDistance(
          state.lastPosition.latitude,
          state.lastPosition.longitude,
          position.latitude,
          position.longitude
        );
        // Only add reasonable distances (5m to 10km)
        if (distance > 5 && distance < 10000) {
          state.accumulatedDistance += distance;
        }
      }
      
      // Track speed stats during trip
      if (isNowMoving) {
        state.maxSpeed = Math.max(state.maxSpeed, position.speed);
        state.speedSum += position.speed;
        state.speedCount++;
      }
      
      // State transition: stopped -> moving (end stop, start trip)
      if (!wasMoving && isNowMoving) {
        // End current stop if exists
        if (state.currentStopStart) {
          const stopDuration = Math.round(
            (new Date(position.fixTime).getTime() - new Date(state.currentStopStart.time).getTime()) / 1000
          );
          
          // Only add stops that are at least 1 minute long
          if (stopDuration >= 60) {
            const newStop: Stop = {
              deviceId: position.deviceId,
              deviceName: device.name,
              duration: stopDuration,
              startTime: state.currentStopStart.time,
              endTime: position.fixTime,
              address: state.currentStopStart.address || position.address || '',
              latitude: state.currentStopStart.lat,
              longitude: state.currentStopStart.lon,
              engineHours: 0,
            };
            
            // Check if we're updating an existing ongoing stop or adding new
            const existingStopIndex = newStops.findIndex(
              s => s.deviceId === position.deviceId &&
                   s.startTime === state.currentStopStart?.time
            );
            mutateStops();
            if (existingStopIndex >= 0) {
              newStops[existingStopIndex] = newStop;
            } else {
              newStops.push(newStop);
            }
          }
          state.currentStopStart = undefined;
        }

        // Start new trip
        state.currentTripStart = {
          time: position.fixTime,
          lat: position.latitude,
          lon: position.longitude,
          address: position.address,
        };
        state.accumulatedDistance = 0;
        state.maxSpeed = position.speed;
        state.speedSum = position.speed;
        state.speedCount = 1;
      }
      
      // State transition: moving -> stopped (end trip, start stop)
      if (wasMoving && !isNowMoving) {
        // End current trip if exists
        if (state.currentTripStart) {
          const tripDuration = Math.round(
            (new Date(position.fixTime).getTime() - new Date(state.currentTripStart.time).getTime()) / 1000
          );
          
          // Only add trips that are at least 30 seconds and moved at least 50m
          if (tripDuration >= 30 && state.accumulatedDistance >= 50) {
            const avgSpeed = state.speedCount > 0 ? state.speedSum / state.speedCount : 0;
            
            const newTrip: Trip = {
              deviceId: position.deviceId,
              deviceName: device.name,
              distance: state.accumulatedDistance,
              averageSpeed: avgSpeed,
              maxSpeed: state.maxSpeed,
              duration: tripDuration,
              startTime: state.currentTripStart.time,
              endTime: position.fixTime,
              startAddress: state.currentTripStart.address || '',
              endAddress: position.address || '',
              startLat: state.currentTripStart.lat,
              startLon: state.currentTripStart.lon,
              endLat: position.latitude,
              endLon: position.longitude,
            };
            
            // Check if we're updating an existing ongoing trip or adding new
            const existingTripIndex = newTrips.findIndex(
              t => t.deviceId === position.deviceId &&
                   t.startTime === state.currentTripStart?.time
            );
            mutateTrips();
            if (existingTripIndex >= 0) {
              newTrips[existingTripIndex] = newTrip;
            } else {
              newTrips.push(newTrip);
            }
          }
          state.currentTripStart = undefined;
        }

        // Start new stop
        state.currentStopStart = {
          time: position.fixTime,
          lat: position.latitude,
          lon: position.longitude,
          address: position.address,
        };
      }
      
      // Update ongoing trip/stop with current position
      if (isNowMoving && state.currentTripStart) {
        const tripDuration = Math.round(
          (new Date(position.fixTime).getTime() - new Date(state.currentTripStart.time).getTime()) / 1000
        );
        const avgSpeed = state.speedCount > 0 ? state.speedSum / state.speedCount : 0;

        const existingTripIndex = newTrips.findIndex(
          t => t.deviceId === position.deviceId && t.startTime === state.currentTripStart?.time
        );

        const ongoingTrip: Trip = {
          deviceId: position.deviceId,
          deviceName: device.name,
          distance: state.accumulatedDistance,
          averageSpeed: avgSpeed,
          maxSpeed: state.maxSpeed,
          duration: tripDuration,
          startTime: state.currentTripStart.time,
          endTime: position.fixTime,
          startAddress: state.currentTripStart.address || '',
          endAddress: position.address || '',
          startLat: state.currentTripStart.lat,
          startLon: state.currentTripStart.lon,
          endLat: position.latitude,
          endLon: position.longitude,
        };

        if (existingTripIndex >= 0) {
          mutateTrips();
          newTrips[existingTripIndex] = ongoingTrip;
        } else if (tripDuration >= 30) {
          mutateTrips();
          newTrips.push(ongoingTrip);
        }
      }

      if (!isNowMoving && state.currentStopStart) {
        const stopDuration = Math.round(
          (new Date(position.fixTime).getTime() - new Date(state.currentStopStart.time).getTime()) / 1000
        );

        const existingStopIndex = newStops.findIndex(
          s => s.deviceId === position.deviceId && s.startTime === state.currentStopStart?.time
        );

        const ongoingStop: Stop = {
          deviceId: position.deviceId,
          deviceName: device.name,
          duration: stopDuration,
          startTime: state.currentStopStart.time,
          endTime: position.fixTime,
          address: state.currentStopStart.address || position.address || '',
          latitude: state.currentStopStart.lat,
          longitude: state.currentStopStart.lon,
          engineHours: 0,
        };

        if (existingStopIndex >= 0) {
          mutateStops();
          newStops[existingStopIndex] = ongoingStop;
        } else if (stopDuration >= 60) {
          mutateStops();
          newStops.push(ongoingStop);
        }
      }
      
      // Update state
      state.isMoving = isNowMoving;
      state.lastPosition = position;
    });
    
    return { trips: newTrips, stops: newStops };
  }, []);
  
  const initializeFromPositions = useCallback((positions: Position[]) => {
    // Initialize device states from current positions
    positions.forEach(position => {
      const isMoving = position.speed >= MOVEMENT_THRESHOLD;
      const state: DeviceActivityState = {
        isMoving,
        accumulatedDistance: 0,
        maxSpeed: position.speed,
        speedSum: position.speed,
        speedCount: 1,
        lastPosition: position,
      };
      
      // If stopped, mark current stop start
      if (!isMoving) {
        state.currentStopStart = {
          time: position.fixTime,
          lat: position.latitude,
          lon: position.longitude,
          address: position.address,
        };
      } else {
        state.currentTripStart = {
          time: position.fixTime,
          lat: position.latitude,
          lon: position.longitude,
          address: position.address,
        };
      }
      
      deviceStatesRef.current.set(position.deviceId, state);
    });
  }, []);
  
  return {
    processPositionUpdates,
    initializeFromPositions,
  };
}
